import { gql } from "@apollo/client";
import { ApiService } from "./ApiService";
import { OpResult, OP_RESULT_CODES } from "@sdflc/api-helpers";
import { User } from "./UserService";

// GraphQL queries
const ME_QUERY = gql`
  query Me {
    me {
      code
      data {
        id
        username
        email
        emailConfirmation
        uiLanguage
        timeZone
        firstName
        middleName
        lastName
        avatarUrl
        birthday
        status
        userAccounts {
          accountId
          userRoleId
          userRole {
            roleName
          }
        }
        account {
          id
          trialEndsAt
          currentPlan
          appPlanPriceId
          psSubscriptionStatus
          appPlan {
            planName
            planFeaturesDetails {
              featureCode
              featureName
              featureValueType
              featureValue
              status
            }
          }
        }
        userRole {
          id
          roleName
        }
        token
        refreshToken
        fingerprintToken
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

// GraphQL mutations
const SIGN_IN_MUTATION = gql`
  mutation SignIn($params: SignInInput) {
    signIn(params: $params) {
      code
      data {
        id
        username
        email
        emailConfirmation
        uiLanguage
        timeZone
        firstName
        middleName
        lastName
        avatarUrl
        birthday
        status
        userAccounts {
          accountId
          userRoleId
          userRole {
            roleName
          }
        }
        account {
          id
          trialEndsAt
          currentPlan
          appPlanPriceId
          psSubscriptionStatus
          appPlan {
            planName
            planFeaturesDetails {
              featureCode
              featureName
              featureValueType
              featureValue
              status
            }
          }
        }
        userRole {
          id
          roleName
        }
        token
        refreshToken
        fingerprintToken
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const SIGN_UP_MUTATION = gql`
  mutation SignUp($params: SignUpInput) {
    signUp(params: $params) {
      code
      data {
        id
        username
        email
        emailConfirmation
        uiLanguage
        timeZone
        firstName
        middleName
        lastName
        avatarUrl
        birthday
        status
        userAccounts {
          accountId
          userRoleId
          userRole {
            roleName
          }
        }
        account {
          id
          trialEndsAt
          currentPlan
          appPlanPriceId
          psSubscriptionStatus
          appPlan {
            planName
            planFeaturesDetails {
              featureCode
              featureName
              featureValueType
              featureValue
              status
            }
          }
        }
        userRole {
          id
          roleName
        }
        token
        refreshToken
        fingerprintToken
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const REFRESH_TOKEN_MUTATION = gql`
  mutation RefreshToken {
    refreshToken {
      code
      errors {
        name
        errors
        warnings
      }
      data {
        id
        username
        email
        emailConfirmation
        uiLanguage
        timeZone
        firstName
        middleName
        lastName
        avatarUrl
        birthday
        status
        userAccounts {
          accountId
          userRoleId
          userRole {
            roleName
          }
        }
        account {
          id
          trialEndsAt
          currentPlan
          appPlanPriceId
          psSubscriptionStatus
          appPlan {
            planName
            planFeaturesDetails {
              featureCode
              featureName
              featureValueType
              featureValue
              status
            }
          }
        }
        userRole {
          id
          roleName
        }
        token
        refreshToken
        fingerprintToken
      }
    }
  }
`;

const SIGN_OUT_MUTATION = gql`
  mutation SignOut {
    signOut {
      code
      data
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const RESET_PASSWORD_REQUEST_MUTATION = gql`
  mutation ResetPasswordRequest($params: ResetPasswordRequestInput) {
    resetPasswordRequest(params: $params) {
      code
      data
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const RESET_PASSWORD_MUTATION = gql`
  mutation ResetPassword($params: ResetPasswordInput) {
    resetPassword(params: $params) {
      code
      data
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

// Types
export interface SignInParams {
  username: string;
  password: string;
}

export interface SignUpParams {
  companyName: string;
  email: string;
  password: string;
  repeatPassword: string;
  username: string;
  urlQuery?: string;
  httpReferer?: string;
}

export interface ResetPasswordRequestParams {
  username: string;
}

export interface ResetPasswordParams {
  requestId: string;
  password: string;
  repeatPassword: string;
}

/**
 * Authentication service that handles all auth-related API calls
 * Uses HTTP-only cookies for authentication (managed by server)
 */
export class AuthService {
  /**
   * Get current user information if authenticated
   * Uses HTTP-only cookies to verify authentication
   */
  static async me(): Promise<OpResult> {
    const result = await ApiService.executeQuery(ME_QUERY);
    return result;
  }

  /**
   * Sign in user with username and password
   */
  static async signIn(params: SignInParams): Promise<OpResult> {
    const result = await ApiService.executeMutation(SIGN_IN_MUTATION, {
      params,
    });

    // Note: Authentication cookies are set by the server automatically
    // No need to store tokens in localStorage since they're HTTP-only cookies

    return result;
  }

  /**
   * Sign up new user
   */
  static async signUp(params: SignUpParams): Promise<OpResult> {
    const result = await ApiService.executeMutation(SIGN_UP_MUTATION, {
      params,
    });

    // Note: Authentication cookies are set by the server automatically
    // No need to store tokens in localStorage since they're HTTP-only cookies

    return result;
  }

  /**
   * Refresh authentication token using HTTP-only cookies
   */
  static async refreshToken(): Promise<OpResult> {
    // No parameters needed since refresh token is in HTTP-only cookies
    const result = await ApiService.executeMutation(REFRESH_TOKEN_MUTATION);

    // If refresh failed, clear user session data
    if (result.didFail()) {
      AuthService.signOut();
    }

    return result;
  }

  /**
   * Sign out user and clear session data
   * Calls server to clear HTTP-only cookies and clears local data
   */
  static async signOut(): Promise<OpResult> {
    try {
      // Call server signOut mutation to clear HTTP-only cookies
      const result = await ApiService.executeMutation(SIGN_OUT_MUTATION);

      // Always clear local session data regardless of server response
      ApiService.clearSession();
      AuthService.clearStoredUserData();

      return result;
    } catch (error) {
      console.error("Error during sign out:", error);

      // Even if server call fails, clear local data
      ApiService.clearSession();
      AuthService.clearStoredUserData();

      // Return error result but still consider it a successful local cleanup
      return new OpResult({
        code: OP_RESULT_CODES.EXCEPTION,
        data: null,
        errors: [
          {
            name: "",
            errors: ["Sign out completed locally, but server error occurred"],
          },
        ],
      });
    }
  }

  /**
   * Request password reset link to be sent to user's email
   */
  static async resetPasswordRequest(
    params: ResetPasswordRequestParams
  ): Promise<OpResult> {
    const result = await ApiService.executeMutation(
      RESET_PASSWORD_REQUEST_MUTATION,
      {
        params,
      }
    );

    return result;
  }

  /**
   * Reset user password using the reset request ID
   */
  static async resetPassword(params: ResetPasswordParams): Promise<OpResult> {
    const result = await ApiService.executeMutation(RESET_PASSWORD_MUTATION, {
      params,
    });

    return result;
  }

  /**
   * Check if user appears to be authenticated
   * Note: This is a best guess since we can't read HTTP-only cookies
   * The server will validate actual authentication
   */
  static isAuthenticated(): boolean {
    // Since we can't read HTTP-only cookies, we check if user data exists
    const userData = localStorage.getItem("venueboards_user");
    return Boolean(userData);
  }

  /**
   * Get stored user data (not tokens, as they're in HTTP-only cookies)
   */
  static getStoredUserData(): User | null {
    try {
      const userData = localStorage.getItem("venueboards_user");
      return userData ? JSON.parse(userData) : null;
    } catch (error) {
      console.error("Error parsing stored user data:", error);
      return null;
    }
  }

  /**
   * Store user data in localStorage (not tokens)
   */
  static storeUserData(userData: User): void {
    // Remove sensitive data before storing
    const { token, refreshToken, fingerprintToken, ...safeUserData } = userData;

    localStorage.setItem("venueboards_user", JSON.stringify(safeUserData));
  }

  /**
   * Clear stored user data
   */
  static clearStoredUserData(): void {
    localStorage.removeItem("venueboards_user");
  }
}
